import React, { useState, useEffect } from 'react';
import {
  collection, onSnapshot, addDoc, updateDoc, doc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../servicios/firebase';
import BotonVolver from '../components/ui/BotonVolver';
import { FaPlus, FaEdit, FaTimes, FaBus, FaUser, FaToggleOn, FaToggleOff, FaIdCard, FaExchangeAlt } from 'react-icons/fa';

// Capacidad por defecto de una urban en Ángeles del Puerto.
const CAPACIDAD_DEFAULT = 16;

const ESTADOS = [
  { value: 'activo',    label: 'Activo',    cls: 'bg-green-100 text-green-700'  },
  { value: 'en_taller', label: 'En taller', cls: 'bg-yellow-100 text-yellow-700'},
  { value: 'baja',      label: 'Baja',      cls: 'bg-red-100 text-red-500'      },
];

export default function Flota() {
  const [tab, setTab] = useState('duenos');

  const [duenos,    setDuenos]    = useState([]);
  const [vehiculos, setVehiculos] = useState([]);
  const [choferes,  setChoferes]  = useState([]);

  // Modales
  const [mDueno,     setMDueno]     = useState(null); // null | 'nuevo' | objeto
  const [mVehiculo,  setMVehiculo]  = useState(null);
  const [mReasignar, setMReasignar] = useState(null); // chofer objeto | null

  // Formularios
  const [fd, setFd] = useState({ nombre: '', telefono: '', banco: '' });
  const [fv, setFv] = useState({ numero: '', capacidad: CAPACIDAD_DEFAULT, duenoId: '', estado: 'activo' });
  const [nuevaUnidadId,  setNuevaUnidadId]  = useState('');
  const [guardando,      setGuardando]      = useState(false);
  const [busquedaDueno,  setBusquedaDueno]  = useState('');
  const [busquedaChofer, setBusquedaChofer] = useState('');

  // ── Cargar datos en tiempo real ────────────────────────────────────────────
  useEffect(() => {
    const u1 = onSnapshot(collection(db, 'duenos'), s =>
      setDuenos(s.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''))));
    const u2 = onSnapshot(collection(db, 'vehiculos'), s =>
      setVehiculos(s.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.numero || 0) - (b.numero || 0))));
    // La lista de usuarios solo es legible por super_admin en Ángeles; si no hay
    // permiso, dejamos choferes en vacío sin romper la página.
    const u3 = onSnapshot(
      collection(db, 'usuarios'),
      s => setChoferes(s.docs.map(d => ({ id: d.id, ...d.data() })).filter(u => u.rol === 'chofer')),
      () => setChoferes([])
    );
    return () => { u1(); u2(); u3(); };
  }, []);

  // ── CRUD Dueños ────────────────────────────────────────────────────────────
  const abrirDueno = (d = null) => {
    setFd(d ? { nombre: d.nombre || '', telefono: d.telefono || '', banco: d.banco || '' }
           : { nombre: '', telefono: '', banco: '' });
    setMDueno(d || 'nuevo');
  };

  const guardarDueno = async (e) => {
    e.preventDefault();
    if (!fd.nombre.trim()) return;
    setGuardando(true);
    try {
      if (mDueno === 'nuevo') {
        await addDoc(collection(db, 'duenos'), { ...fd, activo: true, createdAt: serverTimestamp() });
      } else {
        await updateDoc(doc(db, 'duenos', mDueno.id), { ...fd, updatedAt: serverTimestamp() });
        // Propagar nuevo nombre a sus vehículos
        for (const v of vehiculos.filter(v => v.duenoId === mDueno.id)) {
          await updateDoc(doc(db, 'vehiculos', v.id), { dueno: fd.nombre });
        }
      }
      setMDueno(null);
    } finally { setGuardando(false); }
  };

  const toggleDueno = (d) => updateDoc(doc(db, 'duenos', d.id), { activo: !d.activo });

  // ── CRUD Vehículos ─────────────────────────────────────────────────────────
  const abrirVehiculo = (v = null) => {
    setFv(v ? { numero: v.numero ?? '', capacidad: v.capacidad ?? CAPACIDAD_DEFAULT, duenoId: v.duenoId || '', estado: v.estado || 'activo' }
           : { numero: '', capacidad: CAPACIDAD_DEFAULT, duenoId: '', estado: 'activo' });
    setMVehiculo(v || 'nuevo');
  };

  const guardarVehiculo = async (e) => {
    e.preventDefault();
    if (!fv.numero) return alert('El número de unidad es obligatorio');
    setGuardando(true);
    try {
      const dueno = duenos.find(d => d.id === fv.duenoId);
      const datos = {
        numero:    parseInt(fv.numero),
        capacidad: parseInt(fv.capacidad) || CAPACIDAD_DEFAULT,
        duenoId:   fv.duenoId || null,
        dueno:     dueno?.nombre || null,
        estado:    fv.estado,
        activo:    fv.estado !== 'baja',
      };
      if (mVehiculo === 'nuevo') {
        await addDoc(collection(db, 'vehiculos'), { ...datos, createdAt: serverTimestamp() });
      } else {
        await updateDoc(doc(db, 'vehiculos', mVehiculo.id), { ...datos, updatedAt: serverTimestamp() });
        // Propagar cambios a choferes asignados
        for (const c of choferes.filter(c => c.vehiculoId === mVehiculo.id)) {
          await updateDoc(doc(db, 'usuarios', c.id), {
            vehiculoNumero: datos.numero,
            dueno:          datos.dueno,
          });
        }
      }
      setMVehiculo(null);
    } finally { setGuardando(false); }
  };

  // ── Reasignar chofer a otra unidad ────────────────────────────────────────
  const abrirReasignar = (chofer) => {
    setNuevaUnidadId(chofer.vehiculoId || '');
    setMReasignar(chofer);
  };

  const guardarReasignacion = async (e) => {
    e.preventDefault();
    if (!mReasignar) return;
    setGuardando(true);
    try {
      const vehiculo = vehiculos.find(v => v.id === nuevaUnidadId);
      await updateDoc(doc(db, 'usuarios', mReasignar.id), {
        vehiculoId:     nuevaUnidadId || null,
        vehiculoNumero: vehiculo?.numero || null,
        dueno:          vehiculo?.dueno  || null,
        updatedAt:      serverTimestamp(),
      });
      setMReasignar(null);
    } finally { setGuardando(false); }
  };

  // Helpers
  const estadoCls   = (v) => (ESTADOS.find(e => e.value === v?.estado) || ESTADOS[0]).cls;
  const estadoLabel = (v) => (ESTADOS.find(e => e.value === v?.estado) || ESTADOS[0]).label;
  const vsDeD  = (dId) => vehiculos.filter(v => v.duenoId === dId);
  const csDeV  = (vId) => choferes.filter(c => c.vehiculoId === vId);

  // ══════════════════════════════════════════════════════════════════════════
  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto min-h-screen bg-gray-50">

      {/* HEADER */}
      <div className="mb-6 bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex flex-wrap justify-between items-center gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Gestión de Flota</h1>
          <p className="text-gray-400 text-sm mt-1">
            {duenos.filter(d => d.activo).length} dueños &middot;&nbsp;
            {vehiculos.filter(v => v.activo).length} unidades activas &middot;&nbsp;
            {vehiculos.filter(v => v.estado === 'en_taller').length} en taller
          </p>
        </div>
        <BotonVolver />
      </div>

      {/* TABS */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {[
          { id: 'duenos',   icon: <FaUser />,   label: 'Dueños'   },
          { id: 'unidades', icon: <FaBus />,    label: 'Unidades' },
          { id: 'choferes', icon: <FaIdCard />, label: `Choferes${choferes.length ? ` (${choferes.length})` : ''}` },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-5 py-3 rounded-xl font-bold transition ${
              tab === t.id
                ? 'bg-slate-900 text-white shadow'
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* ──────── TAB DUEÑOS ──────── */}
      {tab === 'duenos' && (
        <>
          <div className="flex flex-wrap gap-3 mb-5">
            <button
              onClick={() => abrirDueno()}
              className="flex items-center gap-2 bg-slate-900 hover:bg-slate-700 text-white px-5 py-3 rounded-xl font-bold transition active:scale-95"
            >
              <FaPlus /> Nuevo Dueño
            </button>
            <div className="relative flex-1 min-w-48">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
              <input
                type="text"
                value={busquedaDueno}
                onChange={e => setBusquedaDueno(e.target.value)}
                placeholder="Buscar dueño…"
                className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-xl bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[...duenos]
              .filter(d => !busquedaDueno || d.nombre?.toLowerCase().includes(busquedaDueno.toLowerCase()))
              .sort((a, b) => vsDeD(b.id).length - vsDeD(a.id).length)
              .map(d => (
              <div
                key={d.id}
                className={`bg-white rounded-2xl border p-5 shadow-sm transition ${!d.activo ? 'opacity-40 border-gray-100' : 'border-gray-200'}`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-full bg-blue-100 text-blue-600 font-black flex items-center justify-center flex-shrink-0">
                      {d.nombre?.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-800">{d.nombre}</h3>
                      {d.telefono && <p className="text-xs text-gray-400">{d.telefono}</p>}
                      {d.banco    && <p className="text-xs text-gray-400">{d.banco}</p>}
                    </div>
                  </div>

                  <div className="flex items-center gap-1 ml-2">
                    <button
                      onClick={() => abrirDueno(d)}
                      className="p-2 text-blue-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                    >
                      <FaEdit />
                    </button>
                    <button
                      onClick={() => toggleDueno(d)}
                      title={d.activo ? 'Desactivar' : 'Activar'}
                      className={`p-2 rounded-lg transition text-xl ${d.activo ? 'text-green-500 hover:bg-green-50' : 'text-gray-300 hover:bg-gray-50'}`}
                    >
                      {d.activo ? <FaToggleOn /> : <FaToggleOff />}
                    </button>
                  </div>
                </div>

                {/* Unidades del dueño */}
                <div className="border-t border-dashed border-gray-100 pt-3">
                  <p className="text-xs text-gray-400 font-bold uppercase mb-2">
                    {vsDeD(d.id).length} unidad{vsDeD(d.id).length !== 1 ? 'es' : ''}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {vsDeD(d.id).map(v => (
                      <button
                        key={v.id}
                        onClick={() => { setTab('unidades'); abrirVehiculo(v); }}
                        title={`Editar unidad ${v.numero}`}
                        className={`px-3 py-1.5 rounded-lg text-sm font-black border transition hover:scale-105 active:scale-95 ${
                          v.estado === 'en_taller' ? 'bg-yellow-50 text-yellow-700 border-yellow-300'  :
                          v.estado === 'baja'      ? 'bg-red-50 text-red-400 border-red-200 opacity-60' :
                          'bg-slate-900 text-white border-slate-700'
                        }`}
                      >
                        {v.numero}
                        {v.estado === 'en_taller' && ' 🔧'}
                        {v.estado === 'baja'      && ' ✕'}
                      </button>
                    ))}
                    {vsDeD(d.id).length === 0 && (
                      <span className="text-xs text-gray-300 italic">Sin unidades registradas</span>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {duenos.length === 0 && (
              <div className="col-span-2 text-center py-16 text-gray-300">
                <FaUser className="text-5xl mx-auto mb-3" />
                <p className="font-bold">No hay dueños registrados</p>
                <p className="text-sm">Crea el primer dueño con el botón de arriba</p>
              </div>
            )}
            {duenos.length > 0 && busquedaDueno &&
              duenos.filter(d => d.nombre?.toLowerCase().includes(busquedaDueno.toLowerCase())).length === 0 && (
              <div className="col-span-2 text-center py-16 text-gray-300">
                <p className="font-bold">Sin coincidencias para "{busquedaDueno}"</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* ──────── TAB UNIDADES ──────── */}
      {tab === 'unidades' && (
        <>
          <div className="flex flex-wrap gap-3 mb-5">
            <button
              onClick={() => abrirVehiculo()}
              className="flex items-center gap-2 bg-slate-900 hover:bg-slate-700 text-white px-5 py-3 rounded-xl font-bold transition active:scale-95"
            >
              <FaPlus /> Nueva Unidad
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {vehiculos.map(v => (
              <div
                key={v.id}
                className={`bg-white rounded-2xl border p-5 shadow-sm transition ${v.estado === 'baja' ? 'opacity-40 border-gray-100' : 'border-gray-200'}`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 rounded-2xl bg-slate-900 text-white font-black text-2xl flex items-center justify-center flex-shrink-0">
                      {v.numero}
                    </div>
                    <div>
                      <p className="font-bold text-gray-800 text-sm leading-tight">{v.dueno || <span className="text-gray-300 italic text-xs">Sin dueño</span>}</p>
                      <p className="text-xs text-gray-400 mt-0.5">Cap. {v.capacidad} pasajeros</p>
                    </div>
                  </div>
                  <button
                    onClick={() => abrirVehiculo(v)}
                    className="p-2 text-blue-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition flex-shrink-0"
                  >
                    <FaEdit />
                  </button>
                </div>

                <div className="flex justify-between items-center border-t border-dashed border-gray-100 pt-3">
                  <span className={`text-xs font-bold px-2 py-1 rounded-full ${estadoCls(v)}`}>
                    {estadoLabel(v)}
                  </span>
                  {csDeV(v.id).length > 0 && (
                    <span className="text-xs text-gray-500 font-bold truncate max-w-[55%]">
                      🧑 {csDeV(v.id).map(c => c.nombre).join(', ')}
                    </span>
                  )}
                </div>
              </div>
            ))}

            {vehiculos.length === 0 && (
              <div className="col-span-3 text-center py-16 text-gray-300">
                <FaBus className="text-5xl mx-auto mb-3" />
                <p className="font-bold">No hay unidades registradas</p>
                <p className="text-sm">Crea la primera unidad con el botón de arriba</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* ──────── TAB CHOFERES ──────── */}
      {tab === 'choferes' && (
        <>
          {/* Buscador */}
          <div className="flex flex-wrap gap-3 mb-5 items-center">
            <div className="relative flex-1 min-w-48">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
              <input
                type="text"
                value={busquedaChofer}
                onChange={e => setBusquedaChofer(e.target.value)}
                placeholder="Buscar chofer…"
                className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-xl bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
              />
            </div>
            <p className="text-sm text-gray-400 font-bold">
              {choferes.length} registrado{choferes.length !== 1 ? 's' : ''} ·{' '}
              {choferes.filter(c => !c.vehiculoId).length} sin unidad asignada
            </p>
          </div>

          {choferes.length === 0 ? (
            <div className="text-center py-20 text-gray-300">
              <FaIdCard className="text-5xl mx-auto mb-3" />
              <p className="font-bold">No hay choferes registrados</p>
              <p className="text-sm mt-1">Crea choferes (rol "chofer") desde la sección <span className="font-bold text-gray-400">Equipo</span> del menú.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {choferes
                .filter(c => !busquedaChofer || c.nombre?.toLowerCase().includes(busquedaChofer.toLowerCase()))
                .sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '', 'es'))
                .map(c => {
                  const vehiculo = vehiculos.find(v => v.id === c.vehiculoId);
                  const sinUnidad = !c.vehiculoId || !vehiculo;
                  return (
                    <div
                      key={c.id}
                      className={`bg-white rounded-2xl border p-5 shadow-sm transition ${sinUnidad ? 'border-orange-200' : 'border-gray-200'}`}
                    >
                      {/* Avatar + nombre */}
                      <div className="flex items-start justify-between gap-3 mb-4">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-11 h-11 rounded-xl bg-indigo-100 text-indigo-700 font-black flex items-center justify-center text-lg flex-shrink-0">
                            {(c.nombre || '?')[0].toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="font-bold text-gray-800 truncate">{c.nombre || '—'}</p>
                            <p className="text-xs text-gray-400 truncate">{c.email || ''}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => abrirReasignar(c)}
                          title="Cambiar unidad"
                          className="p-2 text-indigo-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition flex-shrink-0"
                        >
                          <FaExchangeAlt />
                        </button>
                      </div>

                      {/* Unidad asignada */}
                      <div className={`rounded-xl p-3 flex items-center gap-3 ${sinUnidad ? 'bg-orange-50' : 'bg-gray-50'}`}>
                        {sinUnidad ? (
                          <>
                            <span className="text-orange-400 text-xl">⚠️</span>
                            <p className="text-sm text-orange-600 font-bold">Sin unidad asignada</p>
                          </>
                        ) : (
                          <>
                            <div className="w-10 h-10 rounded-xl bg-slate-900 text-white font-black text-lg flex items-center justify-center flex-shrink-0">
                              {vehiculo.numero}
                            </div>
                            <div className="min-w-0">
                              <p className="font-bold text-gray-700 text-sm">Urban #{vehiculo.numero}</p>
                              <p className="text-xs text-gray-400 truncate">{vehiculo.dueno || 'Sin dueño'}</p>
                              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${estadoCls(vehiculo)}`}>
                                {estadoLabel(vehiculo)}
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          )}

          {/* Unidades sin chofer */}
          {vehiculos.filter(v => v.activo && csDeV(v.id).length === 0).length > 0 && (
            <details className="mt-6 bg-white rounded-2xl border border-dashed border-gray-200 p-4">
              <summary className="text-sm text-gray-400 cursor-pointer font-bold select-none">
                {vehiculos.filter(v => v.activo && csDeV(v.id).length === 0).length} unidades sin chofer asignado
              </summary>
              <div className="mt-3 flex flex-wrap gap-2">
                {vehiculos
                  .filter(v => v.activo && csDeV(v.id).length === 0)
                  .map(v => (
                    <span key={v.id} className="bg-gray-100 text-gray-600 font-black text-sm px-3 py-1.5 rounded-lg">
                      #{v.numero}
                    </span>
                  ))}
              </div>
            </details>
          )}
        </>
      )}

      {/* ════════ MODAL REASIGNAR CHOFER ════════ */}
      {mReasignar && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 w-full max-w-md shadow-2xl">
            <div className="flex justify-between items-center mb-5">
              <h3 className="font-black text-lg">Cambiar unidad</h3>
              <button onClick={() => setMReasignar(null)} className="p-2 text-gray-400 hover:text-gray-700">
                <FaTimes />
              </button>
            </div>

            {/* Info chofer */}
            <div className="flex items-center gap-3 bg-indigo-50 rounded-xl p-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-indigo-100 text-indigo-700 font-black flex items-center justify-center text-lg flex-shrink-0">
                {(mReasignar.nombre || '?')[0].toUpperCase()}
              </div>
              <div>
                <p className="font-bold text-gray-800">{mReasignar.nombre}</p>
                <p className="text-xs text-gray-400">
                  Actualmente: {mReasignar.vehiculoNumero ? `Urban #${mReasignar.vehiculoNumero}` : 'Sin unidad'}
                </p>
              </div>
            </div>

            <form onSubmit={guardarReasignacion} className="space-y-4">
              <div>
                <label className="text-xs font-bold text-gray-400 uppercase block mb-1">Nueva unidad asignada</label>
                <select
                  value={nuevaUnidadId}
                  onChange={e => setNuevaUnidadId(e.target.value)}
                  className="w-full p-3 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-indigo-300"
                >
                  <option value="">— Sin asignar —</option>
                  {vehiculos
                    .filter(v => v.activo !== false)
                    .sort((a, b) => (a.numero || 0) - (b.numero || 0))
                    .map(v => (
                      <option key={v.id} value={v.id}>
                        #{v.numero} — {v.dueno || 'Sin dueño'}
                        {csDeV(v.id).length > 0 && v.id !== mReasignar.vehiculoId
                          ? ` (ya tiene: ${csDeV(v.id).map(c => c.nombre).join(', ')})`
                          : ''}
                      </option>
                    ))}
                </select>
                {nuevaUnidadId && csDeV(nuevaUnidadId).length > 0 && nuevaUnidadId !== mReasignar.vehiculoId && (
                  <p className="text-xs text-orange-500 font-bold mt-1">
                    ⚠️ Esta unidad ya tiene {csDeV(nuevaUnidadId).length} chofer(es) asignado(s).
                    Pueden coexistir (rotación).
                  </p>
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setMReasignar(null)}
                  className="flex-1 py-3 rounded-xl border border-gray-200 font-bold text-gray-600 hover:bg-gray-50 transition"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={guardando}
                  className="flex-1 py-3 rounded-xl bg-slate-900 text-white font-bold disabled:opacity-60 transition hover:bg-slate-700"
                >
                  {guardando ? 'Guardando…' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ════════ MODAL DUEÑO ════════ */}
      {mDueno && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 w-full max-w-md shadow-2xl">
            <div className="flex justify-between items-center mb-5">
              <h3 className="font-black text-lg">
                {mDueno === 'nuevo' ? 'Nuevo Dueño' : `Editar: ${mDueno.nombre}`}
              </h3>
              <button onClick={() => setMDueno(null)} className="p-2 text-gray-400 hover:text-gray-700">
                <FaTimes />
              </button>
            </div>

            <form onSubmit={guardarDueno} className="space-y-4">
              <div>
                <label className="text-xs font-bold text-gray-400 uppercase block mb-1">Nombre completo *</label>
                <input
                  value={fd.nombre}
                  onChange={e => setFd({ ...fd, nombre: e.target.value })}
                  className="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-300"
                  placeholder="Ej. Eduardo Verduzco Ruiz"
                  required
                />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-400 uppercase block mb-1">Teléfono</label>
                <input
                  value={fd.telefono}
                  onChange={e => setFd({ ...fd, telefono: e.target.value })}
                  className="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-300"
                  placeholder="9511234567"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-400 uppercase block mb-1">Banco / CLABE</label>
                <input
                  value={fd.banco}
                  onChange={e => setFd({ ...fd, banco: e.target.value })}
                  className="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-300"
                  placeholder="BBVA · 123456789012345678"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setMDueno(null)}
                  className="flex-1 py-3 rounded-xl border border-gray-200 font-bold text-gray-600 hover:bg-gray-50 transition"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={guardando}
                  className="flex-1 py-3 rounded-xl bg-slate-900 text-white font-bold disabled:opacity-60 transition hover:bg-slate-700"
                >
                  {guardando ? 'Guardando…' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ════════ MODAL VEHÍCULO ════════ */}
      {mVehiculo && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 w-full max-w-md shadow-2xl">
            <div className="flex justify-between items-center mb-5">
              <h3 className="font-black text-lg">
                {mVehiculo === 'nuevo' ? 'Nueva Unidad' : `Editar Unidad ${mVehiculo.numero}`}
              </h3>
              <button onClick={() => setMVehiculo(null)} className="p-2 text-gray-400 hover:text-gray-700">
                <FaTimes />
              </button>
            </div>

            <form onSubmit={guardarVehiculo} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-gray-400 uppercase block mb-1">Número *</label>
                  <input
                    type="number"
                    min="1"
                    value={fv.numero}
                    onChange={e => setFv({ ...fv, numero: e.target.value })}
                    className="w-full p-3 border border-gray-200 rounded-xl font-black text-2xl text-center focus:outline-none focus:ring-2 focus:ring-slate-300"
                    placeholder="4"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-400 uppercase block mb-1">Capacidad</label>
                  <input
                    type="number"
                    min="1"
                    max="60"
                    value={fv.capacidad}
                    onChange={e => setFv({ ...fv, capacidad: e.target.value })}
                    className="w-full p-3 border border-gray-200 rounded-xl font-bold text-center focus:outline-none focus:ring-2 focus:ring-slate-300"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-gray-400 uppercase block mb-1">Dueño</label>
                <select
                  value={fv.duenoId}
                  onChange={e => setFv({ ...fv, duenoId: e.target.value })}
                  className="w-full p-3 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-slate-300"
                >
                  <option value="">— Sin asignar —</option>
                  {duenos.filter(d => d.activo).map(d => (
                    <option key={d.id} value={d.id}>{d.nombre}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-xs font-bold text-gray-400 uppercase block mb-1">Estado</label>
                <div className="grid grid-cols-3 gap-2">
                  {ESTADOS.map(e => (
                    <button
                      key={e.value}
                      type="button"
                      onClick={() => setFv({ ...fv, estado: e.value })}
                      className={`py-3 rounded-xl text-sm font-bold border transition ${
                        fv.estado === e.value
                          ? 'bg-slate-900 text-white border-slate-900'
                          : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {e.label}
                    </button>
                  ))}
                </div>
                {fv.estado === 'en_taller' && (
                  <p className="text-xs text-yellow-600 font-bold mt-2">La unidad aparecerá visible pero marcada en el escáner.</p>
                )}
                {fv.estado === 'baja' && (
                  <p className="text-xs text-red-500 font-bold mt-2">La unidad quedará inactiva y no aparecerá en el escáner.</p>
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setMVehiculo(null)}
                  className="flex-1 py-3 rounded-xl border border-gray-200 font-bold text-gray-600 hover:bg-gray-50 transition"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={guardando}
                  className="flex-1 py-3 rounded-xl bg-slate-900 text-white font-bold disabled:opacity-60 transition hover:bg-slate-700"
                >
                  {guardando ? 'Guardando…' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
